import { notify } from '@/lib/toast';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useAuthStore } from '@/stores/auth.store';
import api from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { Clock, LogIn, LogOut, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { PaginationBar } from '@/components/ui/pagination-bar';
import { PageHeader } from '@/components/ui/page-header';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { EmptyState } from '@/components/ui/empty-state';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';

const STATUS_FILTERS = ['ALL', 'PENDING', 'APPROVED', 'REJECTED'] as const;

interface TimeEntryRow {
  id: string;
  userId: string;
  clockIn: string;
  clockOut?: string | null;
  hoursWorked?: number | string | null;
  status?: string | null;
  notes?: string | null;
  rejectionReason?: string | null;
}

export function TimeAttendancePage() {
  const { t } = useTranslation('employees');
  const { hasPermission } = useAuthStore();
  const canRead = hasPermission('time-attendance:read') || hasPermission('time-attendance:*');
  const canWrite = hasPermission('time-attendance:write') || hasPermission('time-attendance:*');
  const canApprove = hasPermission('time-attendance:approve') || hasPermission('time-attendance:*');
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<(typeof STATUS_FILTERS)[number]>('ALL');
  const qc = useQueryClient();

  const { data: status } = useQuery({
    queryKey: ['time-attendance-status'],
    queryFn: () => api.get('/time-attendance/my-status').then((r) => r.data),
    enabled: canRead,
  });

  const { data: users = [] } = useQuery<{ id: string; firstName: string; lastName: string; email: string }[]>({
    queryKey: ['users-time-attendance'],
    queryFn: () => api.get('/users', { params: { limit: 200 } }).then((r) => r.data?.data ?? r.data ?? []),
    enabled: canApprove,
  });

  const userNameById = (userId: string) => {
    const u = users.find((x) => x.id === userId);
    if (!u) return '—';
    return [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email;
  };

  const { data: entries, isLoading } = useQuery<{ data: TimeEntryRow[]; total: number }>({
    queryKey: ['time-attendance', page, statusFilter],
    queryFn: () => api.get('/time-attendance', {
      params: { page, limit: 20, ...(statusFilter !== 'ALL' && { status: statusFilter }) },
    }).then((r) => r.data),
    enabled: canRead,
  });

  const { data: pending } = useQuery({
    queryKey: ['time-attendance-pending-count'],
    queryFn: () => api.get('/time-attendance/pending-count').then((r) => r.data),
    enabled: canApprove,
  });

  const clockInMutation = useMutation({
    mutationFn: () => api.post('/time-attendance/clock-in', {}),
    onSuccess: () => {
      toast.success('Clocked in successfully');
      qc.invalidateQueries({ queryKey: ['time-attendance-status'] });
      qc.invalidateQueries({ queryKey: ['time-attendance'] });
    },
    onError: (err) => notify.error(err, 'Failed to clock in'),
  });

  const clockOutMutation = useMutation({
    mutationFn: () => api.post('/time-attendance/clock-out', {}),
    onSuccess: () => {
      toast.success('Clocked out successfully');
      qc.invalidateQueries({ queryKey: ['time-attendance-status'] });
      qc.invalidateQueries({ queryKey: ['time-attendance'] });
      qc.invalidateQueries({ queryKey: ['time-attendance-pending-count'] });
    },
    onError: (err) => notify.error(err, 'Failed to clock out'),
  });

  const approveMutation = useMutation({
    mutationFn: (id: string) => api.patch(`/time-attendance/${id}/approve`),
    onSuccess: () => {
      toast.success('Timesheet approved');
      qc.invalidateQueries({ queryKey: ['time-attendance'] });
      qc.invalidateQueries({ queryKey: ['time-attendance-pending-count'] });
    },
    onError: (err) => notify.error(err, 'Failed to approve'),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => api.patch(`/time-attendance/${id}/reject`, { reason }),
    onSuccess: () => {
      toast.success('Timesheet rejected');
      qc.invalidateQueries({ queryKey: ['time-attendance'] });
      qc.invalidateQueries({ queryKey: ['time-attendance-pending-count'] });
    },
    onError: (err) => notify.error(err, 'Failed to reject'),
  });

  if (!canRead) {
    return <div className="p-8 text-center text-muted-foreground">You don't have permission to view time attendance.</div>;
  }

  const isClockedIn = status?.isClockedIn;

  const statusBadge = (entry: TimeEntryRow) => {
    if (!entry.clockOut) return <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-success/10 text-success-text">Active</span>;
    switch (entry.status) {
      case 'APPROVED':
        return <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-success/10 text-success-text">Approved</span>;
      case 'REJECTED':
        return <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-destructive/10 text-destructive-text">Rejected</span>;
      default:
        return <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-warning/10 text-warning-text">Pending</span>;
    }
  };

  return (
    <div className="space-y-6 animate-fade-in-up">
      <PageHeader
        title="Time & Attendance"
        subtitle="Track employee clock-in and clock-out times"
      />

      {canApprove && pending?.count > 0 && (
        <div className="rounded-lg border border-warning/30 bg-warning/10 p-4 flex items-center justify-between">
          <p className="text-sm text-warning-text">
            <strong>{pending.count}</strong> timesheet{pending.count === 1 ? '' : 's'} awaiting your approval. Payroll only counts approved hours.
          </p>
          <Button variant="secondary" size="sm" onClick={() => { setStatusFilter('PENDING'); setPage(1); }}>Review</Button>
        </div>
      )}

      {canWrite && (
        <div className="rounded-lg border p-6 bg-muted/30 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`h-3 w-3 rounded-full ${isClockedIn ? 'bg-success' : 'bg-destructive'}`} />
            <div>
              <p className="font-medium">{isClockedIn ? 'Currently Clocked In' : 'Not Clocked In'}</p>
              {isClockedIn && status?.entry?.clockIn && (
                <p className="text-sm text-muted-foreground">Since {formatDate(status.entry.clockIn)}</p>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            {!isClockedIn ? (
              <Button onClick={() => clockInMutation.mutate()} disabled={clockInMutation.isPending} className="gap-2">
                <LogIn className="h-4 w-4" /> Clock In
              </Button>
            ) : (
              <Button onClick={() => clockOutMutation.mutate()} disabled={clockOutMutation.isPending} variant="secondary" className="gap-2">
                <LogOut className="h-4 w-4" /> Clock Out
              </Button>
            )}
          </div>
        </div>
      )}

      <div className="flex items-center gap-1">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => { setStatusFilter(f); setPage(1); }}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${statusFilter === f ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/70'}`}
          >
            {f.charAt(0) + f.slice(1).toLowerCase()}
          </button>
        ))}
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <SkeletonTable rows={5} cols={6} />
          ) : !entries?.data?.length ? (
            <EmptyState
              icon={Clock}
              title="No time entries yet"
              subtitle="Clock in to start tracking attendance."
            />
          ) : (
            <div className="overflow-x-auto">
              <Table mobileCards>
                <THead>
                  <Tr hover={false}>
                    {canApprove && (
                      <Th>{t('colEmployee')}</Th>
                    )}
                    <Th>{t('colClockIn')}</Th>
                    <Th>{t('colClockOut')}</Th>
                    <Th>{t('colHours')}</Th>
                    <Th>{t('colStatus')}</Th>
                    <Th>{t('colNotes')}</Th>
                    {canApprove && <Th className="text-end">{t('colActions')}</Th>}
                  </Tr>
                </THead>
                <TBody>
                  {entries.data.map((entry) => (
                    <Tr key={entry.id}>
                      {canApprove && (
                        <Td label={t('colEmployee')} className="text-foreground font-medium">{userNameById(entry.userId)}</Td>
                      )}
                      <Td label={t('colClockIn')} className="text-foreground">{formatDate(entry.clockIn)}</Td>
                      <Td label={t('colClockOut')} className="text-muted-foreground">{entry.clockOut ? formatDate(entry.clockOut) : '—'}</Td>
                      <Td label={t('colHours')}>{entry.hoursWorked != null ? `${entry.hoursWorked}h` : '—'}</Td>
                      <Td label={t('colStatus')}>{statusBadge(entry)}</Td>
                      <Td label={t('colNotes')} className="text-muted-foreground">{entry.rejectionReason ? `Rejected: ${entry.rejectionReason}` : entry.notes ?? '—'}</Td>
                      {canApprove && (
                        <Td label={t('colActions')} className="text-end whitespace-nowrap">
                          {entry.clockOut && entry.status === 'PENDING' ? (
                            <div className="inline-flex gap-1">
                              <Button size="sm" variant="ghost" className="h-7 px-2 text-success-text" disabled={approveMutation.isPending} onClick={() => approveMutation.mutate(entry.id)}>
                                <Check className="h-4 w-4" />
                              </Button>
                              <Button size="sm" variant="ghost" className="h-7 px-2 text-destructive-text" disabled={rejectMutation.isPending} onClick={() => {
                                const reason = window.prompt('Reason for rejecting this timesheet?') ?? '';
                                rejectMutation.mutate({ id: entry.id, reason });
                              }}>
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : <span className="text-muted-foreground text-xs">—</span>}
                        </Td>
                      )}
                    </Tr>
                  ))}
                </TBody>
              </Table>
            </div>
          )}
          {entries && entries.total > 20 && (
            <div className="border-t border-border px-4 py-3">
              <PaginationBar
                page={page}
                pageSize={20}
                total={entries.total}
                onPageChange={setPage}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
